import React from 'react';
import { Button, FormSchemaEntry, SvgIcon, UI } from '@gik/ui';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/create/i18n/en';
import { translationKeys as validationTranslationKeys } from '@gik/ui/Form/i18n/en';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import { IRecipientTypeCardProps } from './components/AboutStep/AboutStep';
import SituationTile from './components/SituationStep/SituationTile';
import { ICreateFlowFormValues } from '@gik/create/models/CreateFlowFormValues';
import { recipientFullNameField } from '@gik/core/form/formSchemas';
import { gofundmeRegex } from '@gik/core/utils/GoFundMe';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import { RecipientType } from '@gik/create/enums/RecipientType';
import { FeatureCareCalendarInputNames } from '@gik/create/enums/FeatureCareCalendarInputNames';
import { FeatureWishlistInputNames } from '@gik/create/enums/FeatureWishlistInputNames';
import { FeatureDonationsInputNames } from '@gik/create/enums/FeatureDonationsInputNames';
import { PageNameStepInputNames } from '@gik/create/enums/PageNameStepInputNames';
import { RecipientInfoStepInputNames } from '@gik/create/enums/RecipientInfoStepInputNames';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';

export const aboutStepSchema = (isUserLoggedIn: boolean): FormSchemaEntry[] => [
  {
    label: i18n.t(translationKeys.AboutStepRecipientTypeLabel),
    type: 'select-list',
    name: AboutStepInputNames.RecipientType,
    errorName: 'recipient type',
    required: true,
    props: {
      options: [
        {
          label: 'Myself',
          value: RecipientType.Myself,
          image: getAssetsUrl() + 'core/img/recipient/myself.svg',
        },
        {
          label: 'Someone Else',
          value: RecipientType.SomeoneElse,
          image: getAssetsUrl() + 'core/img/recipient/someone-else.svg',
        },
        {
          label: 'Organization',
          value: RecipientType.Organization,
          image: getAssetsUrl() + 'core/img/recipient/organization.svg',
        },
      ],
      render: (item: IRecipientTypeCardProps, selected: boolean) => {
        return <SituationTile horizontal selected={selected} image={item.image} label={item.label} />;
      },
    },
  },
  {
    label: i18n.t(translationKeys.AboutStepEmailLabel),
    type: 'email',
    required: !isUserLoggedIn,
    name: AboutStepInputNames.Email,
    errorName: 'email',
  },
];

export const featureCareCalendarSchema: (formValues: Partial<ICreateFlowFormValues>) => FormSchemaEntry[] = () => [
  {
    type: 'yes-no',
    name: FeatureCareCalendarInputNames.CareCalendarEnabled,
    errorName: 'Care Calendar',
    requiredMessage: i18n.t(validationTranslationKeys.requiredThis),
    required: true,
  },
];

export const featureWishlistSchema: (formValues: Partial<ICreateFlowFormValues>) => FormSchemaEntry[] = () => [
  {
    type: 'yes-no',
    name: FeatureWishlistInputNames.WishlistEnabled,
    errorName: 'Wishlist',
    requiredMessage: i18n.t(validationTranslationKeys.requiredThis),
    required: true,
  },
];

export const featureDonationsSchema: (formValues: Partial<ICreateFlowFormValues>) => FormSchemaEntry[] = () => [
  {
    type: 'yes-no',
    name: FeatureDonationsInputNames.DonationsEnabled,
    errorName: 'Donations',
    requiredMessage: i18n.t(validationTranslationKeys.requiredThis),
    required: true,
  },
];

export const privacyStepSchema: FormSchemaEntry[] = [];

export const pageNameStepSchema: () => FormSchemaEntry[] = () => [
  {
    type: 'text',
    name: PageNameStepInputNames.PageName,
    required: true,
    maxLength: 80,
    errorName: 'Page Name',
    props: {
      placeholder: i18n.t(translationKeys.PageNamePlaceholder),
    },
  },
];

// todo move this function definition to where the schema is used and pass it down to the schema as a function argument
function handleAddAsPageOrganizerInfo(ev: React.MouseEvent<HTMLDivElement>) {
  ev.stopPropagation();
  ev.preventDefault();
  // TODO: i18n
  UI.info(
    <div>
      <p>To allow the recipient to edit your page, add them as an organizer.</p>
      <p>In the Page Settings you can add even more organizers, and decide who receives notification emails.</p>
    </div>,
    {
      title: 'Adding Page Organizers',
      closable: false,
      buttons: ({ close }) => [
        <Button key="close" onClick={close}>
          Got It
        </Button>,
      ],
    }
  );
}

const recipientFullNameCustomProps = (isOrganization: boolean) =>
  ({
    name: RecipientInfoStepInputNames.RecipientName,
    placeholder: i18n.t(
      isOrganization ? translationKeys.OrganizationNamePlaceholder : translationKeys.RecipientNamePlaceholder
    ),
  } as FormSchemaEntry);

export const recipientInfoFormSchema = (isOrganization = false): FormSchemaEntry[] => [
  recipientFullNameField(recipientFullNameCustomProps(isOrganization)),
  {
    type: 'email',
    name: RecipientInfoStepInputNames.RecipientEmail,
    required: true,
    placeholder: i18n.t(
      isOrganization ? translationKeys.OrganizationEmailPlaceholder : translationKeys.RecipientEmailPlaceholder
    ),
  },
  {
    type: 'email',
    name: RecipientInfoStepInputNames.RecipientEmailConfirm,
    required: true,
    mustEqual: RecipientInfoStepInputNames.RecipientEmail,
    placeholder: i18n.t(
      isOrganization
        ? translationKeys.OrganizationEmailConfirmPlaceholder
        : translationKeys.RecipientEmailConfirmPlaceholder
    ),
  },
  {
    type: 'checkbox',
    name: RecipientInfoStepInputNames.AddAsPageOrganizer,
    props: {
      label: (
        <span className="tw-inline-block">
          {i18n.t(translationKeys.AddAsPageOrganizerLabel)}&nbsp;&nbsp;
          <SvgIcon name="InformationCircle" onClick={handleAddAsPageOrganizerInfo} />
        </span>
      ),
    },
  },
];

export const pageSetupStepSchema: FormSchemaEntry[] = [];

export const payPalFormSchema: FormSchemaEntry[] = [
  {
    type: 'email',
    name: PayPalFormInputNames.EmailAddress,
    placeholder: 'email@domain.com',
    required: false,
  },
];

export const goFundMeFormSchema: () => FormSchemaEntry[] = () => [
  {
    type: 'text',
    name: GoFundMeFormInputNames.PageURL,
    placeholder: 'gofundme.com/f/page',
    required: false,
    customValidation: [
      {
        validate: (value: string) => !gofundmeRegex.test(value),
        message: i18n.t(translationKeys.InvalidGoFundMeURL),
      },
    ],
  },
];
